// Pure helpers for the invoice form's Property & Building sections. Kept out of
// InvoiceFormModal.jsx so they can be unit-tested without dragging the modal's
// PDF previewer into Jest.
import { resolveRateType, flatAmountOf } from "./Api";

export const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const DEFAULT_GST_RATE = 5;

/**
 * Rebuild the form sections from an invoice's line items.
 *
 * An invoice is stored as one document per line, but it was ENTERED as property
 * sections each holding unit rows. Regrouping the lines by property + building
 * and laying their rows back out shows the invoice exactly the way it was
 * created. Both View and Edit use this.
 *
 * Sections and rows both keep the order the items arrive in, which the server
 * has already resolved from lineIndex (falling back to creation order for
 * invoices raised before batching).
 *
 * `editable` decides whether a row pins itself to the amount on record:
 *   - View (default) keeps `storedAmount`, so a line priced before the current
 *     fields existed still shows the real charge instead of a re-derived $0.
 *   - Edit must NOT carry it. rowSubtotal() prefers storedAmount whenever it is
 *     present, so an editable row that kept it would show the old figure no
 *     matter what the user typed — the totals would never move.
 */
export const groupsFromItems = (items = [], { editable = false } = {}) => {
  const map = new Map();
  items.forEach((item) => {
    const ref = (Array.isArray(item?.properties) ? item.properties : [])[0] || item?.property || {};
    const propertyId = ref.propertyId ? String(ref.propertyId) : "";
    const buildingName = ref.buildingName || "";
    // Every line carries its own rate type; a section is purely property + building.
    const rateType = resolveRateType(item);
    const key = `${propertyId}::${buildingName}`;
    if (!map.has(key)) {
      // `title` is resolved server-side by enrichInvoices, so the View can name
      // the property even when the viewer's own property list does not contain
      // it — an auditor's list is empty, and a Select would render blank.
      map.set(key, {
        id: uid(),
        propertyId,
        buildingName,
        propertyLabel: ref.title || item?.propertyTitle || "",
        rows: [],
      });
    }
    const row = {
      id: uid(),
      // The line this row was built from. Edit mode sends it back as the line's
      // `_id` so the server updates that document; the read-only View never
      // uses it.
      itemId: item?._id,
      rateType,
      unitNo: ref.unitNo || "",
      jobDescription: ref.jobDescription || "",
      hours: item?.hours ?? "",
      minutes: item?.minutes ?? "",
      amountPerHour: item?.amountPerHour ?? "",
      flatAmount: rateType === "flat" ? flatAmountOf(item) : "",
      materialCost: item?.materialCost ?? "",
      gstRate: item?.gstRate ?? DEFAULT_GST_RATE,
      comment: item?.comment || "",
      files: [],
    };
    if (!editable) {
      // What the invoice actually records, so a flat-priced line is not
      // re-derived to zero from absent hours and rate.
      row.storedAmount = Number.isFinite(Number(item?.amount)) ? Number(item.amount) : undefined;
    }
    map.get(key).rows.push(row);
  });
  return Array.from(map.values());
};

/**
 * The `lines` array for PUT /invoice/group/:groupId/lines, straight from the
 * form sections. A row that came from the server carries `itemId` and is sent
 * with `_id` so the server updates that document; a row added in the form has
 * none and is created. Only the live pricing field set is sent, mirroring the
 * create payload, so a flat line can never carry a stale hourly rate.
 */
export const linesPayloadFromGroups = (groups = []) =>
  groups.flatMap((group) =>
    (group.rows || []).map((row) => {
      const rateType = row.rateType || "hourly";
      return {
        ...(row.itemId ? { _id: row.itemId } : {}),
        propertyId: group.propertyId,
        buildingName: (group.buildingName || "").trim(),
        unitNo: (row.unitNo || "").trim(),
        jobDescription: (row.jobDescription || "").trim(),
        rateType,
        ...(rateType === "flat"
          ? { flatAmount: Number(row.flatAmount) }
          : {
              hours: Number(row.hours) || 0,
              minutes: Number(row.minutes) || 0,
              amountPerHour: Number(row.amountPerHour),
            }),
        materialCost: Number(row.materialCost) || 0,
        gstRate: Number(row.gstRate),
        comment: (row.comment || "").trim(),
      };
    })
  );
